import { EnhancedReading } from '@/features/equipment-browser/subscriptions/use-get-readings-values';
import {
  getOnOffLabel,
  parseBooleanLike,
} from '@/features/equipment-browser/utils/boolean-display';
import { FormattedUnitLabels } from '@/helpers/unit-formatter/unit-formatter.types';
import { Badge } from '@/ux-common/components/badge/badge';
import { cn } from '@/ux-common/lib/utils';

/**
 * Metric value rendering system for equipment details drawer.
 *
 * This module uses a lookup pattern similar to the card value components
 * (see card-value-components.map.ts) but remains completely independent.
 * While this pattern may seem like overkill for just two renderers, it provides:
 *
 * 1. Consistency: Matches the established pattern used in equipment cards,
 *    making the codebase easier to understand and maintain
 * 2. Extensibility: New display types (e.g., progress bars, status indicators)
 *    can be added without modifying the main component logic
 * 3. Independence: Changes to card rendering won't affect details rendering,
 *    preventing unintended side effects across different UI contexts
 * 4. Testability: Individual renderers can be tested in isolation
 *
 * The parallel structure between cards and details drawer is intentional,
 * even though the implementations remain decoupled.
 */
type MetricValueRenderer = (props: MetricValueRendererProps) => React.ReactNode;

// Renders a badge for boolean values (ON/OFF)
const renderBadgeValue: MetricValueRenderer = ({ reading }) => {
  // TODO(UX-3724): This currently forces On/Off labels; switch to required boolean displayUnits from display_template when that migration lands. https://crossnokaye.atlassian.net/browse/UX-3724
  const value = reading.value;
  const parsedValue = parseBooleanLike(value);
  return (
    <Badge className={cn('badge', parsedValue ? 'badge-good' : 'badge-tier2')}>
      {getOnOffLabel(value, 'upper') ?? 'OFF'}
    </Badge>
  );
};

// Renders default text value with units
const renderDefaultValue: MetricValueRenderer = ({ reading, formattedLabels }) => {
  return (
    <p
      data-truncation-target="true"
      className="max-w-full truncate cursor-inherit font-bold uppercase"
    >
      {formattedLabels.valueLabel}
      {reading?.value !== undefined && (
        <span className="font-medium normal-case">{` ${formattedLabels.unitsLabel}`}</span>
      )}
    </p>
  );
};

interface MetricValueRendererProps {
  reading: EnhancedReading;
  formattedLabels: FormattedUnitLabels;
}

// Map of displayUnits to renderer functions
// 'default' is used as a fallback for any unhandled displayUnits
const metricValueRenderersMap: Record<string, MetricValueRenderer> = {
  Boolean: renderBadgeValue,
  'on-off': renderBadgeValue,
  default: renderDefaultValue,
};

// Gets the appropriate renderer function based on displayUnits
export const getMetricValueRenderer = (displayUnits?: string): MetricValueRenderer => {
  return metricValueRenderersMap[displayUnits ?? 'default'] ?? metricValueRenderersMap.default;
};
